import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Stack from '@mui/material/Stack';
import Form from 'components/Form';
import Section from 'components/Section';
import AccordionController from 'components/AccordionController';
import TextField from 'components/TextField';
import PriceInput from 'components/PriceInput';

import { stringToNumber, textFieldValue, useErrorHandler } from 'lib';
import { ServiceOptionEditForm_serviceOption$key } from '__generated__/ServiceOptionEditForm_serviceOption.graphql';
import { ServiceOptionEditFormMutation } from '__generated__/ServiceOptionEditFormMutation.graphql';

type FormFields = {
  name: string;
  scoroProductId: string;
  price: string;
};

type Props = {
  serviceOption: ServiceOptionEditForm_serviceOption$key;
};

const ServiceOptionEditForm: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const { id, name, scoroProductId, price } = useFragment(
    graphql`
      fragment ServiceOptionEditForm_serviceOption on ServiceOption {
        id
        name
        scoroProductId
        price
      }
    `,
    props.serviceOption
  );

  const [update, updating] = useMutation<ServiceOptionEditFormMutation>(graphql`
    mutation ServiceOptionEditFormMutation(
      $id: ID!
      $input: UpdateServiceOptionInput!
    ) {
      updateServiceOption(id: $id, input: $input) {
        ...EntitySidebar_entity
        ...ServiceOptionEditForm_serviceOption
      }
    }
  `);

  const initial = {
    name,
    scoroProductId: textFieldValue(scoroProductId),
    price: textFieldValue(price),
  };

  const handleSubmit = useCallback(
    (input: FormFields) => {
      update({
        variables: {
          id,
          input: {
            ...input,
            scoroProductId: stringToNumber(input.scoroProductId),
            price: stringToNumber(input.price),
          },
        },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, id, update]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <AccordionController id="serviceOptionEditForm">
          <Section id="main" title="Apie">
            <Stack spacing={4}>
              <TextField autoFocus fullWidth name="name" label="Pavadinimas" />

              <TextField
                fullWidth
                name="scoroProductId"
                label="Scoro produkto ID"
              />

              <TextField
                fullWidth
                name="price"
                label="Kaina"
                component={PriceInput}
              />
            </Stack>
          </Section>
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default ServiceOptionEditForm;
